import React, { useCallback, useState } from "react";
import { CAlert, CCard, CCardBody } from "@coreui/react";
import dayjs from "dayjs";

import { FilterJobsControls } from "@src/components/FilterJobsControls";
import { PaginationControls } from "@src/components/PaginationControls";
import { useEntityFilters } from "@src/hooks/useEntityFilters";
import { useJobs } from "@src/hooks/redux";
import { useApiPagination } from "@src/hooks/usePagination";
import { getEntityConfig } from "@src/services/config/entityConfigs";
import type { TDT } from "@src/types";
import { convertFiltersToQuery } from "@src/utils/api/jobFilters";
import { PAGINATION_STALLED_MESSAGE } from "@src/utils/constants";
import { ACTIVE_JOB_STATUSES } from "@src/utils/jobDisplay";
import { JobsListTable } from "./JobsListTable";

interface Props {}

/**
 * JobsListPage - Displays jobs with direct API fetching (bypasses SmartAPI)
 *
 * ## Data Flow
 * 1. On mount: Direct API fetch with default filters
 * 2. On filter change: New API fetch with updated filters
 * 3. On pagination: useApiPagination fetches more items
 *
 * This pattern ensures fresh data with running_state enrichment from backend.
 * JobPollingManager continues to handle notifications separately.
 */
export const JobsListPage: React.FC<Props> = () => {
  const { jobs: allJobs, setJobs } = useJobs();

  // Use new unified filter/pagination hook
  const { filters, pagination, applyFilters, clearFilters, setPerPage, nextPage, prevPage } =
    useEntityFilters("jobs");

  // Merge new jobs with existing, preserving active jobs for JobPollingManager
  const mergeJobs = useCallback(
    (newJobs: TDT.ApiJob[]) => {
      const newJobIds = new Set(newJobs.map((j) => j.id));
      // Keep active jobs that aren't in the new list
      const activeJobsToPreserve = allJobs.filter(
        (job) => ACTIVE_JOB_STATUSES.includes(job.status_name) && !newJobIds.has(job.id),
      );
      // Merge: preserved active jobs + new jobs (sorted by id desc)
      const merged = [...activeJobsToPreserve, ...newJobs].sort((a, b) => b.id - a.id);
      setJobs(merged);
    },
    [allJobs, setJobs],
  );

  // Get entity config to use its pageSize
  const defaultPageSize = getEntityConfig("jobs").pageSize;

  // Error state for UI feedback
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Unified API hook: handles initial fetch + pagination with single loading state
  // Uses mergeJobs as setItems to preserve active jobs being polled
  const { loading, fetchNextPage, hasMore, error, stalled } = useApiPagination({
    entityName: "jobs",
    currentItems: allJobs,
    setItems: mergeJobs,
    queryFilter: convertFiltersToQuery(filters) || undefined,
  });

  // Show error from hook if any
  React.useEffect(() => {
    if (error) {
      setFetchError("Failed to load jobs. Please try refreshing the page.");
    } else {
      setFetchError(null);
    }
  }, [error]);

  // Client-side filter (applied after API fetch)
  const applyClientSideFilters = React.useCallback(
    (jobs: TDT.ApiJob[]): TDT.ApiJob[] => {
      return jobs.filter((job) => {
        // Default-hide coordinator child backtests (a Monte Carlo permutation run or a CPCV run spawns
        // hundreds; they are an implementation detail of the coordinator, not standalone jobs a user browses).
        if (
          job.metadata?.origin?.type === "monte_carlo_child" ||
          job.metadata?.origin?.type === "walk_forward_cpcv_child"
        ) {
          return false;
        }

        // Filter by job type (inverted logic - hide selected types)
        if (filters.hiddenJobTypes.includes(job.job_type_name)) {
          return false;
        }

        // Filter by status
        if (filters.status && job.status_name !== filters.status) {
          return false;
        }

        // Filter by worker ID (case-insensitive partial match)
        if (filters.workerId && job.worker_id) {
          const workerIdLower = filters.workerId.toLowerCase();
          const jobWorkerIdLower = job.worker_id.toLowerCase();
          if (!jobWorkerIdLower.includes(workerIdLower)) {
            return false;
          }
        } else if (filters.workerId && !job.worker_id) {
          return false;
        }

        // Filter by date range
        const dateFieldValue = filters.dateField === "start_ts" ? job.start_ts : job.complete_ts;
        if (filters.dateRange.from && dateFieldValue) {
          const jobDate = dayjs(dateFieldValue).format("YYYY-MM-DD");
          if (jobDate < filters.dateRange.from) {
            return false;
          }
        }
        if (filters.dateRange.to && dateFieldValue) {
          const jobDate = dayjs(dateFieldValue).format("YYYY-MM-DD");
          if (jobDate > filters.dateRange.to) {
            return false;
          }
        }

        return true;
      });
    },
    [filters],
  );

  // Apply client-side filtering based on applied filters
  const filteredJobs = React.useMemo(() => {
    return applyClientSideFilters(allJobs);
  }, [allJobs, applyClientSideFilters]);

  // Calculate current page items
  const pageJobs = React.useMemo(() => {
    const start = pagination.page * pagination.perPage;
    const end = start + pagination.perPage;
    return filteredJobs.slice(start, end);
  }, [filteredJobs, pagination.page, pagination.perPage]);

  return (
    <CCard id="JobsListPage">
      <CCardBody>
        {fetchError && (
          <CAlert color="danger" dismissible onClose={() => setFetchError(null)}>
            {fetchError}
          </CAlert>
        )}
        <FilterJobsControls
          appliedFilters={filters}
          onApplyFilters={applyFilters}
          onClearFilters={clearFilters}
          disabled={loading}
        />

        <PaginationControls
          itemLabel="jobs"
          currentPage={pagination.page}
          totalItems={filteredJobs.length}
          perPage={pagination.perPage}
          onPerPageChange={setPerPage}
          onPrevPage={prevPage}
          onNextPage={nextPage}
          onNextRequest={fetchNextPage}
          isLoading={loading}
          hasMore={hasMore}
          notice={stalled ? PAGINATION_STALLED_MESSAGE : undefined}
          defaultPageSize={defaultPageSize}
          disabled={loading}
        />

        <JobsListTable jobs={pageJobs} loading={loading} />
      </CCardBody>
    </CCard>
  );
};
